import useI18n from 'src/hooks/useI18n';
import {useSelector, useDispatch} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import * as React from 'react';
import type {
  TranslationFunction,
  MakeKeyFunction,
  LanguageSourceConfig,
} from 'src/types/language-support';
import {selectAgencyData} from 'src/selectors/agency';
import {sendMessageToExtension} from 'src/components/micro-apps-extension/utils';
import {UPDATE_I18N_SELECTED_LANGUAGE} from 'src/chat-extension/messages';

import {getCurrentLanguageForApp} from 'src/utils/language-support';
import {loadLanguageAndTranslationResourceBundle} from 'src/components/lib/i18n/new-i18n';
import {updateAgentSetting} from 'src/action-creators/agent-settings';
import {selectI18NSelectedLanguage} from 'src/selectors/agent-settings';


export default function useLabelTranslation(): {
  t: TranslationFunction;
  makeKey: MakeKeyFunction;
  language: string;
  updateLanguage: (arg1: {
    language: string;
    persistLanguage?: boolean;
    sendUpdatedLanguageToAPI?: boolean;
  }) => Promise<void>;
  getCurrentLanguage: (config?: LanguageSourceConfig) => string;
} {
  const {labelI18nInstance} = useI18n();
  const dispatch = useDispatch();
  const {isExtension} = useEnvironmentContext();

  const agentI18NSelectedLanguage = useSelector(selectI18NSelectedLanguage);

  // read data from store agency.agency so that its available for signin pages
  const {
    // @ts-expect-error - TS2339 - Property 'i18NEnableLanguageSupport' does not exist on type 'Agency'.
    i18NEnableLanguageSupport: isLanguageSupportEnabled,
    // @ts-expect-error - TS2339 - Property 'i18NDefaultLanguage' does not exist on type 'Agency'.
    i18NDefaultLanguage: agencyDefaultLanguage,
  } = useSelector(selectAgencyData);

  const makeKey = (value: string) => {
    try {
      if (value && typeof value === 'string') {
        return value
          .replace(/\(s\)/gi, '_s') // Replace (s) with _s (case-insensitive)
          .replace(/[^\w\s']/g, '') // Remove special chars except apostrophes
          .replace(/\s+/g, '_') // Spaces → underscores
          .replace(/_+/g, '_') // Collapse multiple ____ into _
          .replace(/^_|_$/g, ''); // Trim leading/trailing _
      }
      return value;
    } catch {
      return value;
    }
  };
  const getCurrentLanguage = (config?: LanguageSourceConfig) => {
    return getCurrentLanguageForApp(
      isLanguageSupportEnabled,
      agentI18NSelectedLanguage,
      agencyDefaultLanguage,
      // support local storage language only for non-extension apps
      config
        ? {
            ...config,
            supportLocalStorageLanguage:
              !isExtension && config.supportLocalStorageLanguage,
          }
        : {supportLocalStorageLanguage: !isExtension},
    );
  };

  const updateLanguage = async ({
    // @ts-expect-error - TS7031 - Binding element 'language' implicitly has an 'any' type.
    language,
    persistLanguage = true,
    sendUpdatedLanguageToAPI = true,
  }) => {
    await loadLanguageAndTranslationResourceBundle(language);
    await labelI18nInstance
      // @ts-expect-error - TS2339 - Property 'changeLanguage' does not exist on type 'LabelI18nInstance'.
      .changeLanguage(language)
      .then(() => {
        if (persistLanguage) {
          if (!isExtension) {
            localStorage.setItem('selectedLanguage', language);
          }

          // external apps shouldn't send data to API
          sendUpdatedLanguageToAPI &&
            dispatch(updateAgentSetting('i18NSelectedLanguage', language));
          // when updating the data to API, we should send to extension
          sendUpdatedLanguageToAPI &&
            sendMessageToExtension(UPDATE_I18N_SELECTED_LANGUAGE, language);
        }
      })
      // @ts-expect-error - TS7006 - Parameter 'err' implicitly has an 'any' type.
      .catch((err) => console.error('Error changing language:', err));
  };

  return {
    t: labelI18nInstance.t,
    makeKey,
    language: labelI18nInstance.language,
    updateLanguage,
    getCurrentLanguage,
  };
}
